import {useCallback, useEffect, useMemo, useState } from "react";
import { buildLookupMaps, getColumnValue } from "../utilities/tableHelpers";
import { getBIParams, getDimTableByTableName } from "../services/dimension-service";
import { useGetToken } from "./custom-hooks";

const EMPTY_CONFIG = {
  entity: "",
  title: "",
  tableName: "",
  primaryKey: "",
  labelField: "",
  columns: [],
  relationships: [],
};

const EMPTY_DATA = {
  config: EMPTY_CONFIG,
  rows: [],
};

export const useCommerceDimensionTable = (
  dimTable
) => {
  const token = useGetToken();
  const [dimTableData, setDimTableData] = useState(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("New");
  const [selectedSaleSource, setSelectedSaleSource,] = useState("");

  const [filter, setFilter] = useState("");
  const [lookupFilters, setLookupFilters] = useState({});
  const [columnFilters, setColumnFilters] = useState({});

  const refresh = useCallback(async () => {
    if (!token || !dimTable) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [response] = await Promise.all([
        getDimTableByTableName(dimTable, token),
        getBIParams(token),
      ]);

      setDimTableData(response?.data ?? EMPTY_DATA);
    } catch (requestError) {
      console.error("Unable to load dimension table:", requestError);

      setError(requestError);
      setDimTableData(EMPTY_DATA);
    } finally {
      setIsLoading(false);
    }
  }, [dimTable, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const config = useMemo(
    () => dimTableData?.config ?? EMPTY_CONFIG,
    [dimTableData]
  );

  const rows = useMemo(
    () => dimTableData?.rows ?? [],
    [dimTableData]
  );

  const columns = useMemo(
    () => config.columns ?? [],
    [config.columns]
  );

  const relationships = useMemo(
    () => config.relationships ?? [],
    [config.relationships]
  );

  const visibleColumns = useMemo(
    () =>
      columns
        .filter(
          (column) => column.visible !== false
        )
        .sort(
          (first, second) =>
            (first.sortOrder ?? 999) -
            (second.sortOrder ?? 999)
        ),
    [columns]
  );

  const lookupMaps = useMemo(
    () => buildLookupMaps(relationships),
    [relationships]
  );

  const hasStatuses = useMemo(
    () => rows.some((row) => Boolean(row.status)),
    [rows]
  );

  const hasSaleSources = useMemo(
    () =>
      rows.some((row) =>
        Boolean(row.saleSource)
      ),
    [rows]
  );

  const statuses = useMemo(() => {
    const sourceFilteredRows =
      !selectedSaleSource
        ? rows
        : rows.filter(
            (row) =>
              row.saleSource ===
              selectedSaleSource
          );

    const counts = sourceFilteredRows.reduce(
      (result, row) => {
        if (!row.status) {
          return result;
        }

        result[row.status] =
          (result[row.status] ?? 0) + 1;

        return result;
      },
      {}
    );
    
  return Object.entries(counts)
    .sort(([statusA], [statusB]) =>
      statusA.localeCompare(statusB)
    )
    .map(
      ([status, count]) => ({
        status,
        count,
      })
    );
  }, [rows, selectedSaleSource]);

  useEffect(() => {
    if (!hasStatuses || statuses.length === 0) {
      setSelectedStatus("");
      return;
    }

    const selectedStatusExists = statuses.some(
      ({ status }) => status === selectedStatus
    );

    if (!selectedStatusExists) {
      setSelectedStatus(statuses[0].status);
    }
  }, [
    hasStatuses,
    selectedStatus,
    statuses,
  ]);


  const searchFilteredRows = useMemo(() => {
    const search = filter.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus =
        !hasStatuses ||
        !selectedStatus ||
        row.status === selectedStatus;

      const matchesSearch =
        !search ||
        visibleColumns.some((column) =>
          String(
            getColumnValue(row, column, lookupMaps) ?? ""
          )
            .toLowerCase()
            .includes(search)
        );

      return matchesStatus && matchesSearch;
    });
  }, [
    filter,
    hasStatuses,
    lookupMaps,
    rows,
    selectedStatus,
    visibleColumns,
  ]);

  const updateLookupFilter = useCallback(
    (relationshipField, value) => {
      setLookupFilters((previous) => ({
        ...previous,
        [relationshipField]: value,
      }));
    },
    []
  );

  const hasRelationships = useMemo(
    () =>
      relationships.some(
        (relationship) =>
          relationship.options?.length > 0
      ),
    [relationships]
  );

  const handleColumnFilterChange = useCallback((index, value) => {
    setColumnFilters((previous) => ({
      ...previous,
      [index]: value,
    }));
  }, []);

  const columnFilteredRows = useMemo(() => {
    return searchFilteredRows.filter((row) => {
      return visibleColumns.every((column, index) => {
        const selectedValue = columnFilters[index];

        // No filter selected for this column
        if (
          selectedValue === undefined ||
          selectedValue === null ||
          selectedValue === ""
        ) {
          return true;
        }

        const rowValue = getColumnValue(
          row,
          column,
          lookupMaps
        );

        return String(rowValue ?? "") === String(selectedValue);
      });
    });
  }, [searchFilteredRows, visibleColumns, columnFilters, lookupMaps,]);

  const saleSources = useMemo(() => {
    const counts = new Map();

    columnFilteredRows.forEach((row) => {
      if (!row.saleSource) return;

      counts.set(row.saleSource, (counts.get(row.saleSource) || 0) + 1);
    });

    return Array.from(counts)
      .sort(([saleSourceA], [saleSourceB]) =>
        saleSourceA.localeCompare(saleSourceB)
      )
      .map(([saleSource, count]) => ({
        saleSource,
        count,
      }));
  }, [columnFilteredRows]);

  const filteredRows = useMemo(() => {
    return columnFilteredRows.filter((row) => {
      return (
        !hasSaleSources ||
        !selectedSaleSource ||
        row.saleSource === selectedSaleSource
      );
    });
  }, [
    hasSaleSources,
    columnFilteredRows,
    selectedSaleSource,
  ]);

  return {
    config,
    rows,
    columns,
    relationships,
    visibleColumns,
    lookupMaps,
    filteredRows,
    statuses,
    saleSources,
    hasRelationships,
    isLoading,
    error,
    refresh,
    filter,
    setFilter,
    selectedStatus,
    setSelectedStatus,
    selectedSaleSource,
    setSelectedSaleSource,
    lookupFilters,
    updateLookupFilter,
    columnFilters,
    handleColumnFilterChange,
  };
};